import { useMemo } from "react";
import { useLiveLeagueChoices } from "./useLeagueTable.js";
import { leaguesWithData, matchingSeason, useLeaguesWithFiles } from "./useLeagueGroups.js";
import { leagueOptionInfo } from "../modules/CountryFlag.js";
import { selectedValuesOf, type CategoricalActive } from "../engines/stats-filter-kit.js";
import { chipIsOn } from "../engines/stats-filter-kit.js";
import type { ProtoMetricCategory, ProtoPlayerStats, StatsAllFile, StatsFile } from "@catbento/shared";
import { readSnapshot } from "../data/snapshot.js";
import { getPlayerStats } from "../data/stats.js";
import { KNOWN_LEAGUE_NAMES } from "../live/known-leagues.js";
import { playerNameOf, type PlayerId } from "../data/players.js";
import { LIVE } from "../live/flag.js";
import { liveApi } from "../live/api.js";
import { useLiveQueries, useLiveQuery } from "../live/query.js";
import {
  ALL_COMPETITIONS,
  competitionOptions,
  loadSeasonCompetitions,
  loadSeasonStats,
  loadStatsMeta,
  resolveCompetition,
  resolveSeason,
  type CompetitionOption,
  type LiveSeasonStats,
} from "../live/stats.js";
import {
  bandRanges,
  chipBlocker,
  clubIn,
  countPlayers,
  groupRowsOf,
  groupSeasons,
  hasDetails,
  keepersOf,
  leftOut,
  liveAddMenu,
  LIVE_CHIP_KEYS,
  chipsWithData,
  loadAllFile,
  loadFiles,
  loadFileSeasons,
  loadMovers,
  loadPlayerDetails,
  loadStatsFile,
  seasonLabel,
  LIVE_RANGE_FILTERS,
  type ChipBarSetup,
  type GroupRow,
  type RowPick,
  type SeasonChip,
  type StatsGroup,
  groupValues,
  trackRangesOf,
} from "../live/stats-files.js";
import { getFilterOptions, matchesFilters, type ActiveFilters, type KitPlayer } from "../engines/stats-filter-kit.js";
import { rememberKeeper, rememberWomens, womensFrom } from "../live/player-gender.js";
import type { Range } from "../modules/stats-view.js";

// Phase 02 seam: a player's stats for a season (the flat metricKey→value map +
// season metadata). Category-agnostic on purpose — the snapshot stores ONE flat
// stats map per season, and "category" is a config grouping applied above via
// useMetricCategories (truer to the data than a map-per-category). Returns null
// when the name isn't in the snapshot. Radar reads it (snapshot until its own slice).
export function usePlayerStats(name: string, season?: string): ProtoPlayerStats | null {
  return useMemo(() => getPlayerStats(readSnapshot().players, name, season), [name, season]);
}

/** One Stats tile's values, plus (live only) its competition dropdown: her competitions that
 *  season and the one shown. The snapshot has one league per season, so no dropdown there. */
export interface StatsTile extends ProtoPlayerStats {
  /** `league` = her league that season (the dropdown's default; null in a season without one). */
  competition?: { options: CompetitionOption[]; value: string; league: number | null };
  /** Live: her club now (`/player-current-league`) — the Club chip's first value when she isn't
   *  in the season's file (2b, F3). */
  currentTeam?: string | null;
}

// The Stats tiles' own hook (T2-4), so going live is per WIDGET: Radar keeps `usePlayerStats`.
// The snapshot path is that same lookup (one flat map holds every category); it has no
// competition split, so it ignores the competition asked for.
function useSnapshotStatsTile(playerId: PlayerId | null, season: string | undefined): StatsTile | null {
  return useMemo(
    () => getPlayerStats(readSnapshot().players, playerNameOf(readSnapshot().players, playerId) ?? "", season),
    [playerId, season],
  );
}

const metricKeysOf = (category: ProtoMetricCategory): string[] =>
  category.subcategories.flatMap((s) => s.playerMetrics.map((m) => m.key));

/** Live: any player in the DB. Her seasons + current league once per widget, her competitions
 *  once per season, then one tile's keys for the season + competition shown (a switch asks
 *  only for that). No competition asked for → her league. */
function useLiveStatsTile(
  playerId: PlayerId | null,
  season: string | undefined,
  competition: string | undefined,
  category: ProtoMetricCategory | null,
): (LiveSeasonStats & StatsTile) | null {
  const meta = useLiveQuery(playerId === null ? null : `stats:meta:${playerId}`, () =>
    loadStatsMeta(liveApi(), playerId as PlayerId, KNOWN_LEAGUE_NAMES),
  );
  const seasonCode = meta ? resolveSeason(meta, season) : null;
  const comps = useLiveQuery(meta ? `stats:comps:${playerId}:${seasonCode}` : null, () =>
    loadSeasonCompetitions(liveApi(), playerId as PlayerId, meta!, seasonCode as string),
  );
  // So a drop onto (or from) this tile can tell a man from a woman (T2-14). Same value on
  // every render, so writing it here is safe.
  if (comps) rememberWomens(playerId as PlayerId, womensFrom(comps.competitions));
  const choice = comps ? resolveCompetition(comps, competition) : null;
  const key = comps && category ? `stats:${playerId}:${seasonCode}:${choice}:${category.id}` : null;
  const stats = useLiveQuery(key, () =>
    loadSeasonStats(
      liveApi(),
      playerId as PlayerId,
      meta!,
      seasonCode as string,
      comps!,
      choice as string,
      metricKeysOf(category as ProtoMetricCategory),
    ),
  );
  // So a drop onto a Goalkeeping tile can tell a keeper from an outfield player (Ben, 2026-10-16).
  if (stats) rememberKeeper(playerId as PlayerId, stats.positionTemplate);
  return useMemo(
    () =>
      stats && comps
        ? {
            ...stats,
            competition: { options: competitionOptions(comps), value: choice as string, league: comps.league },
            currentTeam: meta?.current.teamName ?? null,
          }
        : null,
    [stats, comps, choice, meta],
  );
}

/** One Stats tile's values for a player + season (default = her current season) + competition
 *  (default = her league that season). Live with ?live=1 — there it asks only for the tile's own
 *  category. */
export const useStatsTile: (
  playerId: PlayerId | null,
  season: string | undefined,
  competition: string | undefined,
  category: ProtoMetricCategory | null,
) => StatsTile | null = LIVE ? useLiveStatsTile : useSnapshotStatsTile;

/** The live chips that narrow the group: the Season chip (its own state) + the chip bar's others
 *  (Position, Club, Nationality, Age, Minutes). */
export interface GroupChips {
  season: SeasonChip;
  filters: ActiveFilters;
  /** The user removed the League chip (✕): no group until a league is picked. */
  leagueCleared?: boolean;
}

/**
 * Live (T2-12b): the group a tile compares her with, from the nightly Stats files — Ben's rules
 * (`live/stats-files.ts` has the mapping). Her tile's answers are the same keys as useStatsTile,
 * so nothing is asked twice. Then the competition's built seasons, the files of the seasons the
 * Season chip picks (one wait for them all), her gender's track, those seasons' movers lists and
 * her bio (birth date + country: the Age and Nationality chips). A chip change that needs another
 * season asks for that file; the rest is worked out in the browser. Null = nothing to compare (no
 * stats shown).
 */
function useLiveStatsGroup(
  playerId: PlayerId | null,
  season: string | undefined,
  competition: string | undefined,
  category: ProtoMetricCategory | null,
  mode: "per90" | "total",
  chips: GroupChips,
): StatsGroup | null {
  const tile = useLiveStatsTile(playerId, season, competition, category);
  const choice = tile?.competition?.value ?? null;
  // Like for like (Q-003): "All competitions" → her league's All-competitions file; any other
  // choice → that competition's own file.
  const part: "main" | "all" = choice === ALL_COMPETITIONS ? "all" : "main";
  const source = choice === null ? null : part === "all" ? (tile?.competition?.league ?? null) : Number(choice);
  const built = useLiveQuery(source === null ? null : `files:seasons:${source}`, () => loadFileSeasons(liveApi(), source as number));

  const goalkeeping = category?.id === "goalkeeping";
  const herSeason = tile ? Number(tile.seasonCode) : null;
  const builtIds = built ? built.seasons.filter((s) => part === "main" || s.all).map((s) => s.seasonId) : null;
  const picked = builtIds && herSeason !== null ? groupSeasons(chips.season, herSeason, builtIds) : null;
  const seasonsKey = picked && picked.seasons.length > 0 ? picked.seasons.join(",") : null;
  const files = useLiveQuery<(StatsFile | StatsAllFile)[]>(seasonsKey === null ? null : `files:${part}:${source}:${seasonsKey}`, () =>
    part === "all"
      ? loadFiles<StatsFile | StatsAllFile>(picked!.seasons, (s) => loadAllFile(liveApi(), source as number, s))
      : loadFiles<StatsFile | StatsAllFile>(picked!.seasons, (s) => loadStatsFile(liveApi(), source as number, s)),
  );
  // The League chip (Ben, 2026-10-17): with the dropdown on her league, tick other leagues to add
  // their league players (like for like: everyone's league games), each league's own file for the
  // matching season(s). Not offered on a cup or "All competitions" (the group is that competition).
  const choices = useLiveLeagueChoices();
  const leagueAllowed = part === "main" && source !== null && tile?.competition?.league === source;
  const homeLabel = leagueAllowed ? (choices.find((c) => c.id === source)?.label ?? null) : null;
  const leagueChip = chips.filters.league as CategoricalActive | undefined;
  const ticked = leagueAllowed ? selectedValuesOf(leagueChip) : [];
  const leagueEmpty = leagueAllowed && ticked.length === 0 && (!!chips.leagueCleared || Array.isArray(leagueChip));
  const leagueLabels = ticked.length > 0 ? ticked : homeLabel === null || leagueEmpty ? [] : [homeLabel];
  const withHome = !leagueAllowed || (homeLabel !== null && leagueLabels.includes(homeLabel));
  const others = leagueAllowed ? choices.filter((c) => c.label !== homeLabel && leagueLabels.includes(c.label)) : [];
  const unknownLeagues = leagueLabels.filter((l) => l !== homeLabel && !choices.some((c) => c.label === l));
  const otherBuilt = useLiveQueries(
    others.map((c) => ({ key: `files:seasons:${c.id}`, load: () => loadFileSeasons(liveApi(), c.id) })),
  );
  // Each picked season → that league's matching season (Q-006), or none.
  const otherSeasons = others.map((_, i) =>
    (picked?.seasons ?? []).map((s) => matchingSeason(s, otherBuilt[i]!.seasons.map((x) => x.seasonId))),
  );
  const otherReady = others.flatMap((c, i) => {
    const ss = otherSeasons[i]!.filter((s): s is number => s !== null);
    return ss.length > 0 ? [{ league: c, seasons: ss, map: otherSeasons[i]! }] : [];
  });
  const notBuiltLeagues = others.filter((_, i) => otherSeasons[i]!.every((s) => s === null)).map((c) => c.label);
  const otherFiles = useLiveQueries<StatsFile[]>(
    otherReady.map((r) => ({
      key: `files:main:${r.league.id}:${r.seasons.join(",")}`,
      load: () => loadFiles<StatsFile>(r.seasons, (s) => loadStatsFile(liveApi(), r.league.id, s)),
    })),
  );
  // Every file has the players' details + position rows, except "All competitions" files from
  // before 2b (until the full rebuild): then the detail chips aren't offered (F1).
  const details = files ? files.every(hasDetails) : true;
  // Goalkeeping on an old-format "All competitions" file: who's a keeper comes from her league's
  // own files (no position rows there) — the same seasons, already asked for on her league's view.
  // From 2b the file has its own minutes in goal.
  const keeperFiles = useLiveQuery(
    goalkeeping && part === "all" && !details && seasonsKey !== null ? `files:main:${source}:${seasonsKey}` : null,
    () => loadFiles(picked!.seasons, (s) => loadStatsFile(liveApi(), source as number, s)),
  );
  const womens = files?.[0]?.womens;
  // The seasons' movers (one list per season and gender) — left out of the leagues they left.
  const movers = useLiveQuery(womens === undefined || seasonsKey === null ? null : `movers:${womens}:${seasonsKey}`, () =>
    Promise.all(picked!.seasons.map((s) => loadMovers(liveApi(), s, womens as boolean))),
  );
  const her = useLiveQuery(playerId === null ? null : `details:${playerId}`, () => loadPlayerDetails(liveApi(), playerId as PlayerId));

  const metrics = useMemo(() => (category ? category.subcategories.flatMap((s) => s.playerMetrics) : []), [category]);
  // With a Position chip on, the dots are the position rows (minutes AT each position); else each
  // player's whole competition (or whole season, on All).
  const positionOn = chipIsOn(chips.filters.position);
  const otherKey = otherReady.map((r) => `${r.league.id}:${r.seasons.join(",")}`).join("|");
  // The dropdown's own competition + season: the track always (rules.md "Reading a bar"), and the
  // group's base while the League chip includes it.
  const homeRows = useMemo<GroupRow[] | null>(() => {
    if (!files || !movers) return null;
    const keys = metrics.map((m) => m.key);
    return files.flatMap((f, i) => {
      const pick: RowPick = goalkeeping
        ? part === "all" && !hasDetails(f)
          ? { kind: "all", only: keepersOf(keeperFiles![i]!) }
          : { kind: "group", positionGroup: "goalkeeper" }
        : positionOn
          ? { kind: "group" }
          : { kind: part === "all" ? "all" : "total" };
      const out = leftOut(movers[i]!, f.competitionId);
      return groupRowsOf(f, pick, keys).filter((r) => !out.has(r.playerId));
    });
  }, [files, keeperFiles, movers, metrics, part, goalkeeping, positionOn]);
  // The group: the League chip's leagues (Ben, 2026-10-17) — a filter, so it moves the band, never
  // the track. Other ticked leagues add their league players (position rows with the Position chip;
  // keepers on Goalkeeping), minus that season's movers who left them.
  const rows = useMemo<GroupRow[] | null>(() => {
    if (!homeRows || !movers) return null;
    if (leagueEmpty) return [];
    const keys = metrics.map((m) => m.key);
    const extra = otherReady.flatMap((r, ri) =>
      otherFiles[ri]!.flatMap((f) => {
        const i = r.map.indexOf(f.seasonId);
        const pick: RowPick = goalkeeping ? { kind: "group", positionGroup: "goalkeeper" } : positionOn ? { kind: "group" } : { kind: "total" };
        const out = i === -1 ? new Set<number>() : leftOut(movers[i]!, f.competitionId);
        return groupRowsOf(f, pick, keys).filter((row) => !out.has(row.playerId));
      }),
    );
    return withHome ? [...homeRows, ...extra] : extra;
  }, [homeRows, movers, metrics, goalkeeping, positionOn, leagueEmpty, withHome, otherKey, otherFiles]);

  // The chip bar: values from every position row in the files (they carry club + country too), her
  // details for a new chip's first value and the Age chip's centre.
  const herClub = files && herSeason !== null && playerId !== null ? clubIn(files, playerId, herSeason) : null;
  const refPlayer = useMemo<KitPlayer>(
    () => ({
      positionTemplate: tile?.positionTemplate ?? null,
      team: herClub ?? tile?.currentTeam ?? null,
      country: her?.country ?? null,
      dob: her?.dob ?? null,
    }),
    [tile, herClub, her],
  );
  // Her gender's leagues with data for the season(s) shown (Ben, 2026-10-18), and whatever's ticked.
  const withFiles = useLeaguesWithFiles();
  const pickedSeasons = picked?.seasons;
  const womensLeagues = useMemo(
    () => leaguesWithData(choices.filter((c) => c.womens === (womens ?? null)), withFiles, pickedSeasons ?? [], [...ticked, homeLabel]),
    // `ticked` is a fresh array each render; its contents are what count.
    [choices, womens, withFiles, pickedSeasons, ticked.join("|"), homeLabel],
  );
  const options = useMemo(() => {
    const all = [...(files ?? []), ...otherFiles.flat()];
    const o = all.length ? getFilterOptions(all.flatMap((f) => groupRowsOf(f, { kind: "group" }, []))) : {};
    return { ...o, league: womensLeagues.map((c) => c.label) };
  }, [files, otherFiles, womensLeagues]);
  // Q-013: only chips at least half the players (with minutes) have data for.
  const withData = useMemo(() => (files ? chipsWithData(LIVE_CHIP_KEYS, files) : []), [files]);
  const addMenu = liveAddMenu({ goalkeeping, details, herDob: her?.dob ?? null, withData });
  const chipSetup: ChipBarSetup = {
    // League after Season (Ben's order puts it after Position; first here as the group's base).
    addMenu: leagueAllowed ? [addMenu[0]!, "league", ...addMenu.slice(1)].filter((k) => k !== undefined) : addMenu,
    options,
    refPlayer,
    leagueAllowed,
    defaultLeague: homeLabel ?? undefined,
    optionInfo: { league: leagueOptionInfo(womensLeagues) },
  };
  const blocker = her ? chipBlocker(chips.filters, { details, herDob: her.dob }) : null;
  // The League chip picks the files (above); it doesn't filter the rows (they carry no league).
  const rowFilters = useMemo(() => ({ ...chips.filters, league: undefined }), [chips.filters]);
  const filtered = useMemo(
    () => (rows ? rows.filter((r) => matchesFilters(r, rowFilters, refPlayer, LIVE_RANGE_FILTERS)) : null),
    [rows, rowFilters, refPlayer],
  );

  if (!tile || Object.keys(tile.stats).length === 0) return null;
  const seasonOptions = (builtIds ?? []).map((id) => ({ id, label: seasonLabel(id) }));
  const name = tile.competition?.options.find((o) => o.value === choice)?.short ?? "";
  const none = (label: string): StatsGroup => ({ kind: "none", label, seasonOptions, chips: chipSetup });
  if (source === null) return none(`No comparison: no league in ${seasonLabel(Number(tile.seasonCode))}`);
  if (picked === null || filtered === null) {
    // The Season chip asked for a season with no file (not built yet), or nothing is built.
    const asked = picked?.asked ?? herSeason;
    return none(`No comparison yet: ${name} ${seasonLabel(asked as number)} isn't built`);
  }
  if (blocker !== null) return none(blocker);
  if (leagueEmpty) return none("Pick a league");
  if (unknownLeagues.length > 0) return none(`No comparison: ${unknownLeagues.join(", ")} isn't a league with data`);
  if (!withHome && otherReady.length === 0 && notBuiltLeagues.length > 0) {
    return none(`No comparison yet: ${notBuiltLeagues.join(", ")} ${seasonLabel(herSeason as number)} isn't built`);
  }
  const n = picked.seasons.length;
  const missing = notBuiltLeagues.length > 0 ? ` · no data: ${notBuiltLeagues.join(", ")}` : "";
  return {
    kind: "group",
    count: countPlayers(filtered),
    note: (n > 1 ? ` · ${n} seasons` : "") + missing,
    band: bandRanges(filtered, metrics, mode),
    values: groupValues(filtered, metrics, mode),
    // Ben, 2026-10-16 (rules.md "Reading a bar"): the track is the selected league + season —
    // the group before any filter but Season — so with no filters the band fills the bar.
    // The track: the dropdown's league + season only — the League chip, a filter, moves the band.
    // Its 5th → 95th percentile (Ben, 2026-10-18): a few extreme players don't squash the bar.
    track: trackRangesOf(homeRows ?? [], metrics, mode),
    seasonOptions,
    chips: chipSetup,
  };
}

/** The snapshot site has no group: its bars read the demo population (StatsModule). */
function useNoStatsGroup(): StatsGroup | null {
  return null;
}

export const useStatsGroup: (
  playerId: PlayerId | null,
  season: string | undefined,
  competition: string | undefined,
  category: ProtoMetricCategory | null,
  mode: "per90" | "total",
  chips: GroupChips,
) => StatsGroup | null = LIVE ? useLiveStatsGroup : useNoStatsGroup;

/** One player on a comparison tile (T2-14). */
export interface ComparedPlayer {
  id: PlayerId;
  /** The season her numbers are from: her current one ("52026"). */
  seasonCode: string;
  stats: Record<string, number | null>;
  positionTemplate: string | null;
  /** Live: the league her numbers are from (the "?" card names the bar's leagues). */
  league?: string | null;
}

export interface StatsComparisonData {
  players: ComparedPlayer[];
  /** Each bar's full length: every merged player's league + season combined (rules.md). */
  track: Record<string, Range | null>;
}

/**
 * Live (T2-14): 2–5 players on one Stats tile. Each player's own numbers as row 1's defaults
 * show them — her league, the season her tile showed at the merge (Ben, 2026-10-16; was her
 * current one, Prateek 2026-10-15) — through the same calls and
 * keys as a one-player tile, so the tile's own player isn't asked again. One dot each on a track
 * made from their leagues + seasons combined (Ben, 2026-10-16); no group, so no band and no chips
 * (stats.md). The players load side by side, then their leagues' files for the track.
 */
export function useLiveStatsComparison(
  ids: PlayerId[],
  category: ProtoMetricCategory | null,
  // Live only (Ben, 2026-10-16): merged tiles get the Per 90 / Total toggle too.
  mode: "per90" | "total" = "per90",
  // Each player's season as their tile showed it at the merge (`_mergedSeasons`; null = their
  // current one) — so the same player on two seasons is a year-on-year (Ben, 2026-10-16).
  mergedSeasons: readonly (string | null | undefined)[] = [],
): StatsComparisonData | null {
  const leagueNames = KNOWN_LEAGUE_NAMES;
  const metas = useLiveQueries(
    ids.map((id) => ({ key: `stats:meta:${id}`, load: () => loadStatsMeta(liveApi(), id, leagueNames) })),
  );
  const seasons = metas.map((m, i) => resolveSeason(m, mergedSeasons[i] ?? undefined));
  const comps = useLiveQueries(
    ids.map((id, i) => ({
      key: `stats:comps:${id}:${seasons[i]}`,
      load: () => loadSeasonCompetitions(liveApi(), id, metas[i]!, seasons[i]!),
    })),
  );
  const genders = comps.map((c, i) => {
    const womens = womensFrom(c.competitions);
    rememberWomens(ids[i]!, womens);
    return womens;
  });
  // The drop rule only lets in players of one gender, so anything else is a bug (or a data
  // problem) — said out loud, never drawn on the wrong track.
  const womens = genders[0];
  if (womens === undefined || womens === null || genders.some((g) => g !== womens)) {
    throw new Error(`Stats comparison: can't tell one gender for players ${ids.join(", ")} (${genders.join(", ")})`);
  }
  const choices = comps.map((c) => resolveCompetition(c, undefined));
  const stats = useLiveQueries(
    category === null
      ? []
      : ids.map((id, i) => ({
          key: `stats:${id}:${seasons[i]}:${choices[i]}:${category.id}`,
          load: () => loadSeasonStats(liveApi(), id, metas[i]!, seasons[i]!, comps[i]!, choices[i]!, metricKeysOf(category)),
        })),
  );
  // Ben, 2026-10-16 (rules.md "Reading a bar", merged players): the track is every merged player's own
  // league + season, combined — same keys as a one-player tile's files, so nothing is asked twice.
  const sources = [
    ...new Map(
      stats.map((s, i) => [`${choices[i]}:${s.seasonCode}`, { league: Number(choices[i]), season: Number(s.seasonCode) }]),
    ).values(),
  ].filter((x) => Number.isInteger(x.league));
  const builtLists = useLiveQueries(
    sources.map((x) => ({ key: `files:seasons:${x.league}`, load: () => loadFileSeasons(liveApi(), x.league) })),
  );
  const ready = sources.filter((x, i) => builtLists[i]!.seasons.some((b) => b.seasonId === x.season));
  const leagueFiles = useLiveQueries(
    ready.map((x) => ({
      key: `files:main:${x.league}:${x.season}`,
      load: () => loadFiles<StatsFile | StatsAllFile>([x.season], (s) => loadStatsFile(liveApi(), x.league, s)),
    })),
  );
  // So a drop onto a Goalkeeping tile can tell a keeper (Ben, 2026-10-16).
  stats.forEach((st, k) => rememberKeeper(ids[k]!, st.positionTemplate));
  if (category === null) return null;
  const metrics = category.subcategories.flatMap((s) => s.playerMetrics);
  const keys = metrics.map((m) => m.key);
  const pick: RowPick = category.id === "goalkeeping" ? { kind: "group", positionGroup: "goalkeeper" } : { kind: "total" };
  const trackRows = leagueFiles.flat().flatMap((f) => groupRowsOf(f, pick, keys));
  return {
    players: stats.map((s, i) => ({
      id: ids[i]!,
      seasonCode: s.seasonCode,
      stats: s.stats,
      positionTemplate: s.positionTemplate,
      league: comps[i]!.competitions.find((c) => String(c.id) === choices[i])?.name ?? null,
    })),
    track: trackRangesOf(trackRows, metrics, mode),
  };
}
